// @file For special effects for whiteboard
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { deleteShapeMetadata } from '@@/bits/whiteboard'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type { ShapeSpecialEffects } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import { isEmpty } from 'es-toolkit/compat'
import type { JsonObject, TLShape, TLShapeId } from 'tldraw'

/**
 * Handles special effects (sound and visual) when shapes are grouped together.
 * If all shapes in the group have the same effect, that effect is preserved.
 * If shapes have different effects, the effects are removed from all shapes and a snackbar will be shown to allow user to undo.
 *
 * @param shapeIds - Array of shape IDs that are being grouped
 * @param markId - History mark ID for undo functionality
 */
function handleGroupSpecialEffects(shapeIds: TLShapeId[], markId: string): void {
  const editor = getEditorObject()
  if (editor == null) return
  const shapes = shapeIds.map((shapeId) => editor.getShape(shapeId))
  if (shapes.length === 0) return

  const shapeEffects: Array<Array<string | null>> = shapes.map((shape) => {
    const soundEffect = (shape?.meta?.sound_effect as ShapeSpecialEffects['sound_effect'])?.effect_id ?? null
    const visualEffect = (shape?.meta?.visual_effect as ShapeSpecialEffects['visual_effect'])?.effect_id ?? null
    return [soundEffect, visualEffect]
  })
  // Generate the most effective effect by checking the first shape's effects and then checking the rest of the shapes
  // For example, [["rec3SYMvNrFmSSEo6", null], [null, "confetti"]]
  // The effective effect should be ["rec3SYMvNrFmSSEo6", "confetti"]
  const effectiveEffect = [
    shapeEffects.find((effect) => effect[0] !== null)?.[0] ?? null,
    shapeEffects.find((effect) => effect[1] !== null)?.[1] ?? null,
  ]

  // Normalize shape effects by filling in any missing effects with the first shape's effects.
  // This allows us to properly compare effects across shapes, treating null values as matching
  // the first shape's effects. For example, if the first shape has a sound effect but no visual effect,
  // other shapes with no effects will be treated as having the same sound effect.
  const normalizedShapeEffects = shapeEffects.map(([soundEffect, visualEffect]) => ({
    soundEffect: soundEffect ?? effectiveEffect[0],
    visualEffect: visualEffect ?? effectiveEffect[1],
  }))
  const normalizedEffectiveEffect = normalizedShapeEffects[0]
  const allShapesHaveTheSameSoundEffect = new Set(normalizedShapeEffects.map((effect) => effect.soundEffect)).size === 1
  const allShapesHaveTheSameVisualEffect =
    new Set(normalizedShapeEffects.map((effect) => effect.visualEffect)).size === 1

  if (allShapesHaveTheSameSoundEffect) {
    useWhiteboardSpecialEffectsStore().setActiveGroupSpecialEffects({
      soundEffectId: normalizedEffectiveEffect.soundEffect,
    })
  } else {
    // If not all shapes have the same sound effect, remove the sound effect from all shapes
    shapes.forEach((shape) => deleteShapeMetadata((shape as TLShape).id, ['sound_effect']))
  }
  if (allShapesHaveTheSameVisualEffect) {
    useWhiteboardSpecialEffectsStore().setActiveGroupSpecialEffects({
      visualEffectId: normalizedEffectiveEffect.visualEffect,
    })
  } else {
    // If not all shapes have the same visual effect, remove the visual effect from all shapes
    shapes.forEach((shape) => deleteShapeMetadata((shape as TLShape).id, ['visual_effect']))
  }

  let undoMessage = ''

  if (!allShapesHaveTheSameSoundEffect && !allShapesHaveTheSameVisualEffect) {
    undoMessage = __('Special effects have been removed from objects in this group')
  } else if (!allShapesHaveTheSameSoundEffect) {
    undoMessage = __('Sound effects have been removed from objects in this group')
  } else if (!allShapesHaveTheSameVisualEffect) {
    undoMessage = __('Visual effects have been removed from objects in this group')
  }

  if (undoMessage !== '') {
    // Show a snackbar to allow the user to undo the grouping action
    useGlobalSnackbarStore().setSnackbar({
      message: undoMessage,
      actionText: __('Undo'),
      actionTextActions: [() => editor.bailToMark(markId)],
    })
  }
}

/**
 * Updates the special effects (sound and visual) for a given shape
 * @param shapeId - The ID of the shape to update
 * @param specialEffects - Object containing the sound and visual effect IDs to apply
 * @param specialEffects.soundEffectId - ID of the sound effect to apply, or null to remove
 * @param specialEffects.visualEffectId - ID of the visual effect to apply, or null to remove
 */
function updateSpecialEffectsInShape(
  shapeId: TLShapeId,
  specialEffectIds: {
    soundEffectId?: string | null
    visualEffectId?: string | null
  },
): void {
  const editor = getEditorObject()
  if (editor == null) return
  const shape = editor.getShape(shapeId)
  if (shape == null) return

  const meta: ShapeSpecialEffects = {}
  const keysToDelete: string[] = []
  if (specialEffectIds.soundEffectId != null) {
    meta.sound_effect = { type: 'system', effect_id: specialEffectIds.soundEffectId }
  } else {
    keysToDelete.push('sound_effect')
  }
  if (specialEffectIds.visualEffectId != null) {
    meta.visual_effect = { effect_id: specialEffectIds.visualEffectId }
  } else {
    keysToDelete.push('visual_effect')
  }

  if (!isEmpty(meta)) {
    editor.updateShape({
      id: shape.id,
      type: shape.type,
      meta: meta as Partial<JsonObject>,
    })
  }

  if (keysToDelete.length > 0) {
    deleteShapeMetadata(shapeId, keysToDelete)
  }
}

/**
 * Updates the special effects (sound and visual) for all shapes within a group
 * @param groupId - The ID of the group containing the shapes to update
 * @param specialEffectIds - Object containing the sound and visual effect IDs to apply
 * @param specialEffectIds.soundEffectId - ID of the sound effect to apply, or null to remove
 * @param specialEffectIds.visualEffectId - ID of the visual effect to apply, or null to remove
 */
function updateSpecialEffectsInAllShapesInGroup(
  groupId: TLShapeId,
  specialEffectIds: {
    soundEffectId?: string | null
    visualEffectId?: string | null
  },
): void {
  const tldrawEditor = getEditorObject()
  if (tldrawEditor == null) return
  const shapes = tldrawEditor.getCurrentPageShapes().filter((shape) => shape.parentId === groupId)
  shapes.forEach((shape) => {
    updateSpecialEffectsInShape(shape.id, specialEffectIds)
  })
}

export { handleGroupSpecialEffects, updateSpecialEffectsInAllShapesInGroup, updateSpecialEffectsInShape }
